import React, { createContext, useContext, useState, useEffect } from 'react';

import { resolveInitialDark } from '@/shared/lib/embed/embed-view';

interface ThemeContextProps {
  darkMode: boolean;
  toggleDarkMode: () => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [darkMode, setDarkMode] = useState<boolean>(true);

  // 페이지 로드 시 저장된 테마 상태를 로드합니다 (기본값: dark)
  useEffect(() => {
    const saved = localStorage.getItem('darkMode');
    // 앱 «보기» 웹뷰(?embed=1&theme=light|dark)는 앱 테마를 따른다 — 저장하지 않는다(embed-view.ts resolveInitialDark).
    const isDark = resolveInitialDark(saved, window.location.pathname, window.location.search);
    setDarkMode(isDark);
    document.documentElement.classList.toggle('dark', isDark);
  }, []);

  // 다크 모드 토글 함수
  const toggleDarkMode = () => {
    setDarkMode(prev => {
      const newDarkMode = !prev;
      localStorage.setItem('darkMode', newDarkMode.toString());
      if (newDarkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      return newDarkMode;
    });
  };

  return <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>{children}</ThemeContext.Provider>;
};

// 커스텀 훅으로 Context를 쉽게 사용할 수 있게 합니다
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
